import { paginationFragment } from './fragments/pagination';

const fragment = `
  id
  hashId
  name
  countryCode
  country
  stateCode
  state
  city
  domains
  logoUrl
  logoRedirectUrl
  secondaryLogoUrl
  iconUrl
  bannerImageUrl
  archivedAt
  settings {
    design {
      primaryColor
      textColor
      accentColor
      singleFamilyPriceNote
      multiFamilyPriceNote
      floorPlanHighlightColor
      validUntil {
        amount
        period
      }
      lotPremiumLabel
    }
    disclaimerTexts {
      section
      text
      updatedAt
      maxLength
      position
    }
    messages {
      section
      text
      updatedAt
    }
  }
  widgetSettings {
    windowWidth
    windowWidthUnit
    savedHomesFloatingButton {
      enabled
      backgroundColor
      iconColor
      icon
    }
    openWidgetOnSaveFromInlineEmbed
  }
  headerScripts
  footerScripts
  hubSpotSettings {
    hasToken
    contactListId
    lifecycleStage
    leadStatus
    casl
    leadSource
    buildLink
    community
    model
    authUrl
    verifiedStatusField
    reservationStepField
    caslFieldCustomValue
    project
    unit
    portalId
  }
  enableSampleData
  enablePickingALot
  senderEmail
  senderPhoneNumber
  createdAt
  enableNewClientUI
  useStartingFromPrice
  enableHomesFromPriceForCommunities
  enableStartingFromPriceForSfhInventory
  enableStartingFromPriceForMfhInventory
  enableHomeSales
  enableHomeRental
  enableMultiFamilyHomes
  enableSingleFamilyHomes
  enableMultiFamilyHomesRental
  enableSingleFamilyHomesRental
  enableVerifiedBuyingPower
  enableIdVerification
  enableExitPageReminder
  socialMediaLoginSettings {
    facebook {
      appId
      appSecretMasked
    }
    google {
      clientId
      clientSecretMasked
    }
    apple {
      clientId
    }
  }
  enableValidUntil
  termsUrl
  enablePreApprovalVerification
  enableInviteCustomer
  enableRequestCustomerVerification
  enableCaptureLeadsFromBeginning
  enableCORSForJavaScriptSDK
  cookieTrackingScope
  enableContactPhoneRequired
  reserveNowSettings {
    enabled
    enabledReports
    enabledPriceNegotiation
  }
  excludeTrafficSettings {
    excludedIPs
  }
  paymentSettings {
    stripe {
      secretKeyMasked
      publishableKey
      isConnected
      currency
      fixedDepositAmount
      enableImmediatePayout
      paymentMethods
      enableRefunds
      enableHoldFunds
      holdExpirationHours
    }
  }
  llmSettings {
    openAi {
      customerInsightsPrompt
    }
  }
  idVerificationProvider
  preApprovalVerificationProvider
  docuSignSettings {
    enabled
    connectedAt
    userInfo {
      firstName
      lastName
      email
      accounts {
        accountId
        accountName
        isDefault
      }
    }
    workflows {
      inventory {
        enabled
        type
        templateId
        templateName
        emailSubject
      }
      buildPrice {
        enabled
        type
        templateId
        templateName
        emailSubject
      }
    }
  }
  isDocuSignConnected
  emailService
  sendGridSettings {
    apiKeyMasked
    contactAutomationRules {
      addedHomeListId
    }
    webhookUrl
    webhookEnabled
  }
  homePriceDisplayMode
  mortgageRateSettings {
    downPayment
    downPaymentType
    loanTerm
    interestRate
  }
  overrideQuickPossessionSize
  inventoryDocumentToSign {
    id
    name
    fileUrl
  }
  buildPriceDocumentToSign {
    id
    name
    fileUrl
  }
  isDocuSignGenericTemplateEnabled
  leadNotificationSalesRepresentatives @include(if: $withSalesReps) {
    id
    name
    email
  }
  reservationDropOffNotificationSalesReps @include(if: $withSalesReps) {
    id
    name
    email
  }
  enableNewHomeNavigation
  contactStages
  defaultContactStage
  enableDemoModeForHomewise
  defaultHomePage
  reportSettings {
    enabled
    enabledFinancialReport
    enabledFinancialHighlights
    enabledMarketingReport
  }
  enableDemoModeForVerified
  enableDemoModeForReserveNow
  enableAirMilesForVerified
  enableAirMilesForReserveNow
  enableEmbedded
  embeddedBaseUrl
  exportSettings {
    customers
  }
  buyerVerifiedWorkflow @include(if: $withWorkflows) {
    id
    type
    workflow
  }
  rentalVerifiedWorkflow @include(if: $withWorkflows) {
    id
    type
    workflow
  }
  buyerReserveWorkflow @include(if: $withWorkflows) {
    id
    type
    workflow
  }
  rentalReserveWorkflow @include(if: $withWorkflows) {
    id
    type
    workflow
  }
  enableCommunication
  timezone
  bankVerificationProvider
  allowSaveHomeWithoutVerification
  emailMessages {
    savedHomeReservationReminderQP
    savedHomeReservationReminderUnit
  }
  contract @include(if: $withContract) {
    id
    startDate
    renewalDate
    monthlyAmount
    type
    stage
  }
  marketingConsentSettings {
    enabledCheckbox
    defaultChecked
    required
    text
  }
  enableReservationDropOffNotification
  utourBuilderId
  emailNotifications @include(if: $withNotifications) {
    type
    subject
    title
    description
  }
  smsNotifications @include(if: $withNotifications) {
    type
    message
  }
  enableIncompleteReservationReminder
  enableHomeSavedReservationReminder
  enableAutoArchiveInventory
  autoArchiveInventoryDays
  enableVerificationReminder
  browseAiSettings {
    enabled
    autoUnpublish
    mapping {
      communityField
      modelField
      addressField
      priceField
      bedsField
      bathsField
      sqftField
      imageUrlField
      listingUrlField
      descriptionField
      floorPlanFields {
        nameField
        imageUrlField
      }
    }
    robotIds
  }
  onlineSavingsSettings {
    enabled
    type
    value
    showHeadline
    showBanner
    showProfileBanner
    profileBannerText
  }
  enableAppendCityProvince
  showInterestedPeopleCount
`;

export const listCompaniesQuery = `
  query ListCompanies(
    $filter: CompanyFilterInput
    $sort: SortInput
    $page: Int
    $pageSize: Int
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    result: listCompanies(page: $page, pageSize: $pageSize, filter: $filter, sort: $sort) {
      items {
        ${fragment}
      }
      pagination {
        ${paginationFragment}
      }
    }
  }
`;

export const getCompanyQuery = `
  query GetCompany(
    $id: Int!
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    item: getCompany(id: $id) {
      ${fragment}
    }
  }
`;

export const createCompanyQuery = `
  mutation CreateCompany(
    $input: CreateCompanyInput!
    $image: Upload
    $icon: Upload
    $bannerImage: Upload
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    item: createCompany(input: $input, image: $image, icon: $icon, bannerImage: $bannerImage) {
      ${fragment}
    }
  }
`;

export const updateCompanyQuery = `
  mutation UpdateCompany(
    $input: UpdateCompanyInput!
    $image: Upload
    $icon: Upload
    $bannerImage: Upload
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    item: updateCompany(input: $input, image: $image, icon: $icon, bannerImage: $bannerImage) {
      ${fragment}
    }
  }
`;

export const archiveCompanyQuery = `
  mutation ArchiveCompany(
    $id: Int!
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    item: archiveCompany(id: $id) {
      ${fragment}
    }
  }
`;

export const restoreCompanyQuery = `
  mutation RestoreCompany(
    $id: Int!
    $withWorkflows: Boolean! = false
    $withContract: Boolean! = false
    $withNotifications: Boolean! = false
    $withSalesReps: Boolean! = false
  ) {
    item: restoreCompany(id: $id) {
      ${fragment}
    }
  }
`;

export const createDocumentToSignTemplateQuery = `
  mutation CreateDocumentToSignTemplate(
    $id: Int!,
    $input: DocumentToSignTemplateInput!
  ) {
    templateId: createDocumentToSignTemplate(id: $id, input: $input)
  }
`;

export const sendTrackingCodeEmailMutation = `
  mutation SendTrackingCodeEmail(
    $id: Int!
    $email: String!
  ) {
    sent: sendTrackingCodeEmail(id: $id, email: $email)
  }
`;
